import { useRef, useContext, useEffect, WheelEvent } from 'react';
import { useAutomationsSelector } from 'pages/AutomationEngine/store/react';
import {
  getAutomationDebugMode,
  getArchivedContext,
} from 'pages/AutomationEngine/store/selectors';
import { ExecutionHistoryContext } from 'ts-components/AutomationEngine/ExecutionHistory/context';
import { debugByStatus } from 'pages/AutomationEngine/ts-helpers';
import { ExecutionHistory, Step } from 'pages/AutomationEngine/types';
import { DebuggerControls, FailedStepControls } from './DebuggerControls';
import { useVerticalScroll } from 'pages/AutomationEngine/hooks/useVerticalScroll';
import { HistoryDetails } from './HistoryDetails';
import {
  StyledBottomGradient,
  StyledHistoryDetailsList,
  StyledTopGradient,
  StyledHistoryPanel,
} from './styles';
import { AUTOMATION_STEPS } from 'pages/AutomationEngine/steps';

type HistoryPanelProps = {
  historyList: ExecutionHistory[];
  step: Step;
};

export const HistoryPanel = ({ historyList, step }: HistoryPanelProps) => {
  const automationDebugMode: boolean = useAutomationsSelector(
    getAutomationDebugMode
  );
  const archivedContext = useAutomationsSelector(getArchivedContext);

  const refs = useRef<Record<string, HTMLDivElement | null>>({});
  const { containerProps, topFade, bottomFade, scrollDivRef } =
    useVerticalScroll([historyList, step]);

  const { selectedHistoryStep, canEditAutomation } = useContext(
    ExecutionHistoryContext
  );

  // beacuse react-flow is taking over the scroll event
  const handleWheel = (event: WheelEvent<HTMLDivElement>) => {
    if (
      scrollDivRef.current &&
      scrollDivRef.current.offsetHeight < scrollDivRef.current.scrollHeight
    ) {
      event.stopPropagation();
    }
  };

  const selectedHistory =
    historyList.find(({ id }) => selectedHistoryStep?.id === id) ||
    historyList[0];

  useEffect(() => {
    if (selectedHistory && refs.current[selectedHistory.id]) {
      const top = refs.current[selectedHistory.id]?.offsetTop;
      scrollDivRef.current?.scrollTo({ top, behavior: 'smooth' });
    }
  }, [scrollDivRef, selectedHistory]);

  const showDebugControls =
    canEditAutomation &&
    automationDebugMode &&
    !archivedContext &&
    !!selectedHistory &&
    step.type !== AUTOMATION_STEPS.next_or_finish.type &&
    (debugByStatus(selectedHistory.status) ||
      selectedHistory.status === 'paused_by_failure');

  const showExecutionControls =
    !archivedContext && selectedHistory?.status === 'paused_by_failure';

  const showControls = showDebugControls || showExecutionControls;

  return (
    <>
      <StyledHistoryPanel
        showControls={showControls}
        className="kds-expanding-scrollbar"
        onWheelCapture={handleWheel}
        {...containerProps}
        data-qa="card-panel"
        marginRight={
          historyList.length > 2 || (showControls && historyList.length === 2)
            ? '-8px'
            : '0px'
        }
      >
        <StyledTopGradient position="top" fadeIn={topFade} />
        <StyledHistoryDetailsList>
          {historyList.map((history) => (
            <HistoryDetails
              key={history.id}
              history={history}
              step={step}
              historyRef={(el) => (refs.current[history.id] = el)}
            />
          ))}
        </StyledHistoryDetailsList>
        <StyledBottomGradient position="bottom" fadeIn={bottomFade} />
      </StyledHistoryPanel>
      {showDebugControls ? (
        <DebuggerControls step={step} selectedHistory={selectedHistory} />
      ) : showExecutionControls ? (
        <FailedStepControls step={step} />
      ) : null}
    </>
  );
};
